import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchBookmakerDetail, trackBookmakerClick } from '@/api';
import MatchCard from '@/components/match/MatchCard';
import BookmakerSEOContent from '@/components/seo/BookmakerSEOContent';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight, Star, ExternalLink, TrendingUp, BarChart3, Trophy, Globe, Users, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/* ── Brand Assets: accent color, gradient, text style ── */
const BRAND = {
  '1xbet':        { accent: '#1a6cbc', grad: 'linear-gradient(135deg, #0d3b66 0%, #1a6cbc 50%, #2980b9 100%)', logo: '/bookmaker-logos/1xbet-logo.svg', textColor: '#fff', stats: { years: '15+', dailyPlayers: '3M+', countries: '62' } },
  'bet365':       { accent: '#027B5B', grad: 'linear-gradient(135deg, #013d2e 0%, #027B5B 50%, #0a9e76 100%)', textColor: '#ffd700', logoText: 'bet365' },
  'pinnacle':     { accent: '#1B365D', grad: 'linear-gradient(135deg, #0d1b30 0%, #1B365D 50%, #2a5298 100%)', textColor: '#e8c547', logoText: 'PINNACLE' },
  'betfair':      { accent: '#1e1e2f', grad: 'linear-gradient(135deg, #0f0f1a 0%, #1e1e2f 50%, #FFB80C 100%)', textColor: '#FFB80C', logoText: 'betfair' },
  'betway':       { accent: '#00a5b5', grad: 'linear-gradient(135deg, #0a1628 0%, #122640 50%, #00a5b5 100%)', textColor: '#00e5c8', logoText: 'betway' },
  'unibet':       { accent: '#146B2B', grad: 'linear-gradient(135deg, #0a3516 0%, #146B2B 50%, #1e9e40 100%)', textColor: '#a3e635', logoText: 'unibet' },
  'william-hill': { accent: '#002D5B', grad: 'linear-gradient(135deg, #001730 0%, #002D5B 50%, #004d99 100%)', textColor: '#fff', logoText: 'William Hill' },
  'bwin':         { accent: '#1a1a1a', grad: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 40%, #FFD700 100%)', textColor: '#FFD700', logoText: 'bwin' },
  'betsson':      { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #FF6600 100%)', textColor: '#FF6600', logoText: 'betsson' },
  'paddy-power':  { accent: '#00A651', grad: 'linear-gradient(135deg, #005328 0%, #00A651 50%, #00cc66 100%)', textColor: '#fff', logoText: 'Paddy Power' },
  'ladbrokes':    { accent: '#E0001A', grad: 'linear-gradient(135deg, #700010 0%, #E0001A 50%, #ff3344 100%)', textColor: '#fff', logoText: 'Ladbrokes' },
  'betvictor':    { accent: '#1a1a1a', grad: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #cc0033 100%)', textColor: '#cc0033', logoText: 'BetVictor' },
  'betano':       { accent: '#1a3a2e', grad: 'linear-gradient(135deg, #0d1f18 0%, #1a3a2e 50%, #2ecc71 100%)', textColor: '#2ecc71', logoText: 'betano' },
  'marathonbet':  { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0a0a15 0%, #1a1a2e 50%, #3366cc 100%)', textColor: '#4488ee', logoText: 'marathon' },
  '888sport':     { accent: '#1a2e3a', grad: 'linear-gradient(135deg, #0d1820 0%, #1a2e3a 50%, #00857C 100%)', textColor: '#00d4c8', logoText: '888sport' },
  'betfred':      { accent: '#003DA5', grad: 'linear-gradient(135deg, #001f52 0%, #003DA5 50%, #0066ff 100%)', textColor: '#fff', logoText: 'BetFred' },
  'dafabet':      { accent: '#8B0000', grad: 'linear-gradient(135deg, #450000 0%, #8B0000 50%, #cc1a1a 100%)', textColor: '#ffd700', logoText: 'dafabet' },
  'tipico':       { accent: '#003366', grad: 'linear-gradient(135deg, #001a33 0%, #003366 50%, #0066cc 100%)', textColor: '#fff', logoText: 'tipico' },
  'sportingbet':  { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #cc0033 100%)', textColor: '#ff3355', logoText: 'Sportingbet' },
  'superbet':     { accent: '#8B0000', grad: 'linear-gradient(135deg, #3d0000 0%, #8B0000 50%, #cc2222 100%)', textColor: '#ffd700', logoText: 'Superbet' },
  'netbet':       { accent: '#1a3a1a', grad: 'linear-gradient(135deg, #0d1f0d 0%, #1a3a1a 50%, #339933 100%)', textColor: '#66cc66', logoText: 'NetBet' },
  'comeon':       { accent: '#1a2e3a', grad: 'linear-gradient(135deg, #0d1820 0%, #1a2e3a 50%, #4488cc 100%)', textColor: '#66bbff', logoText: 'ComeOn' },
  'nordicbet':    { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #336699 100%)', textColor: '#5599cc', logoText: 'NordicBet' },
  'expekt':       { accent: '#1a2e1a', grad: 'linear-gradient(135deg, #0d180d 0%, #1a2e1a 50%, #339933 100%)', textColor: '#66cc66', logoText: 'Expekt' },
  'fonbet':       { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #FF6600 100%)', textColor: '#FF6600', logoText: 'Fonbet' },
  'interwetten':  { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #cc9900 100%)', textColor: '#ffcc00', logoText: 'Interwetten' },
  '188bet':       { accent: '#1a1a2e', grad: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #cc6600 100%)', textColor: '#ff9933', logoText: '188Bet' },
  'betcris':      { accent: '#003366', grad: 'linear-gradient(135deg, #001a33 0%, #003366 50%, #336699 100%)', textColor: '#66aadd', logoText: 'BetCRIS' },
  'bovada':       { accent: '#8B0000', grad: 'linear-gradient(135deg, #3d0000 0%, #8B0000 50%, #cc1a1a 100%)', textColor: '#ff6644', logoText: 'BOVADA' },
  'bet-10':       { accent: '#1a2e3a', grad: 'linear-gradient(135deg, #0d1820 0%, #1a2e3a 50%, #0099cc 100%)', textColor: '#33ccff', logoText: '10Bet' },
};

function BrandLogo({ slug, name, brand }) {
  if (brand?.logo) {
    return (
      <div className="h-14 md:h-16 px-5 rounded-2xl flex items-center justify-center shadow-xl shadow-black/30 shrink-0"
        style={{ background: brand.accent }} data-testid="bookmaker-logo">
        <img src={brand.logo} alt={name} className="h-6 md:h-7 w-auto object-contain" />
      </div>
    );
  }
  if (brand?.logoText) {
    return (
      <div className="h-14 md:h-16 px-5 rounded-2xl flex items-center justify-center shadow-xl shadow-black/30 shrink-0"
        style={{ background: brand.accent }} data-testid="bookmaker-logo">
        <span className="text-lg md:text-xl font-black tracking-tight whitespace-nowrap"
          style={{ color: brand.textColor, fontFamily: 'var(--font-heading)' }}>
          {brand.logoText}
        </span>
      </div>
    );
  }
  return (
    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center text-2xl font-bold text-emerald-400 shrink-0">
      {name[0]}
    </div>
  );
}

export default function BookmakerPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { currentLang, buildHreflang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBookmakerDetail(slug).then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingPage />;
  if (!data?.bookmaker) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  const { bookmaker, stats, recent_matches } = data;
  const hreflangTags = buildHreflang('bookmaker', slug);
  const brand = BRAND[slug];

  const handleVisitClick = () => {
    trackBookmakerClick(bookmaker.id).catch(() => {});
    const link = bookmaker.affiliate_link || bookmaker.website_url;
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
  };

  const heroStyle = brand?.grad
    ? { background: brand.grad }
    : { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' };

  return (
    <div className="container mx-auto px-4 max-w-7xl py-6" data-testid="bookmaker-page">
      <SEOHead
        title={`${bookmaker.name} Odds`}
        description={`Compare ${bookmaker.name} odds with other bookmakers on ODDSIS.`}
        hreflang={hreflangTags}
      />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
        <Link to={`/${currentLang}`} className="hover:text-slate-300 transition-colors">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">{bookmaker.name}</span>
      </div>

      {/* Hero Card */}
      <div className="relative overflow-hidden rounded-2xl mb-6" style={heroStyle} data-testid="bookmaker-hero">
        <div className="relative z-10 p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-5">
              <BrandLogo slug={slug} name={bookmaker.name} brand={brand} />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-white"
                  style={{ fontFamily: 'var(--font-heading)' }}>
                  {bookmaker.name}
                </h1>
                <div className="flex items-center gap-2 mt-2">
                  {bookmaker.featured && (
                    <Badge className="bg-white/15 text-amber-300 border-amber-400/30 text-xs font-semibold">
                      <Star className="w-3 h-3 mr-1 fill-amber-300" />Featured
                    </Badge>
                  )}
                  {bookmaker.rating > 0 && (
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(bookmaker.rating) ? 'text-amber-300 fill-amber-300' : 'text-white/20'}`} />
                      ))}
                      <span className="text-sm font-semibold text-white/80 ml-1">{bookmaker.rating}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Button onClick={handleVisitClick}
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold shadow-lg shadow-black/20 transition-all hover:scale-[1.02] px-6"
              data-testid="visit-bookmaker-btn">
              <ExternalLink className="w-4 h-4 mr-2" /> {t('bookmaker.visit', { name: bookmaker.name })}
            </Button>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-8">
            <StatCard icon={BarChart3} value={stats.odds_count} label={t('bookmaker.activeOdds')} />
            <StatCard icon={Trophy} value={stats.matches_count} label={t('bookmaker.matchesCovered')} />
            <StatCard icon={TrendingUp} value={stats.best_odds_count} label={t('bookmaker.bestOdds')} />
            {brand?.stats && (
              <>
                <StatCard icon={Zap} value={brand.stats.years} label={t('seoExtra.years', 'Years')} />
                <StatCard icon={Users} value={brand.stats.dailyPlayers} label={t('seoExtra.dailyPlayers', 'Daily Players')} />
                <StatCard icon={Globe} value={brand.stats.countries} label={t('seoExtra.countries', 'Countries')} />
              </>
            )}
          </div>
        </div>
      </div>

      <BookmakerSEOContent slug={slug} />

      <section>
        <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('bookmaker.recentMatches')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {recent_matches?.map(match => (<MatchCard key={match.id} match={match} />))}
        </div>
        {!recent_matches?.length && <p className="text-slate-500 text-sm">{t('bookmaker.noUpcoming')}</p>}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, value, label }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-3 text-center">
      <Icon className="w-4 h-4 text-white/60 mx-auto mb-1.5" />
      <p className="text-lg md:text-xl font-bold tabular-nums text-white">{value}</p>
      <p className="text-[9px] text-white/50 mt-0.5 uppercase tracking-widest leading-tight">{label}</p>
    </div>
  );
}
